import React, { useEffect, useMemo } from "react";

import { Form, Input, Select, Col, Row } from "antd";
import { Check, Loader2 } from "lucide-react";
import countryList from "react-select-country-list";

import { CompanyInfo } from "../../../../bitmaker/schemas/Billing/CompanyInfo";

import { useTerms } from "../../../../hooks/useTerms";

import { CURRENCY_OPTIONS, LANGUAGE_OPTIONS } from "../../../../constants";

import { MyFormItem } from "../../MyFormItem";

interface Props {
  company: CompanyInfo;
  onSave: (updatedCompany: CompanyInfo) => void;
  loading?: boolean;
  submitError?: string | null;
}

export default function CompanyInformationForm({
  company,
  onSave,
  loading = false,
  submitError = null,
}: Props) {
  const [form] = Form.useForm();

  const { terms, loadingTerms, fetchTermsError } = useTerms();

  const countryOptions = useMemo(
    () =>
      countryList()
        .getData()
        .map((c: { label: string }) => ({
          label: c.label,
          value: c.label,
        })),
    [],
  );

  // Load company into form
  useEffect(() => {
    if (company) {
      form.resetFields();
      form.setFieldsValue(company);
    } else {
      form.resetFields();
    }
  }, [company, form]);

  const handleSubmit = (values: CompanyInfo) => {
    if (loading) return;

    const dataToSave = {
      ...values,
      companyName: values.companyName?.trim(),
      corporateName: values.corporateName?.trim() || null,
      defaultTermId: values.defaultTermId ?? null,
      country: values.country ?? null,
      emailAddress: values.emailAddress?.trim() || null,
      billingEmailAddress: values.billingEmailAddress?.trim() || null,
      phoneNumber: values.phoneNumber?.trim() || null,
      addressLine: values.addressLine?.trim() || null,
      townCity: values.townCity?.trim() || null,
      stateProvince: values.stateProvince?.trim() || null,
      postalCode: values.postalCode?.trim() || null,
      taxName: values.taxName?.trim() || null,
      taxNumber: values.taxNumber?.trim() || null,
      defaultNotes: values.defaultNotes?.trim() || null,
    };

    onSave(dataToSave);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col gap-2 mb-6">
        <h2 className="text-2xl text-gray-800">Company Settings</h2>
        <p className="text-gray-600">
          Manage your company's core contact, legal, and default settings.
        </p>
      </div>

      {submitError && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {submitError}
        </div>
      )}

      <Form
        id="companyInfoForm"
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-8"
      >
        {/* Section 1: Identification & Contact */}
        <section className="p-6 bg-white shadow rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Identification & Contact
          </h3>

          <Row gutter={[24, 16]}>
            <Col span={12}>
              <MyFormItem
                label="Company Name"
                name="companyName"
                rules={[{ required: true, message: "Please enter the company name." }]}
              >
                <Input
                  placeholder="Bitmaker"
                  onBlur={(e) => {
                    const value = e.target.value.trim();
                    if (value !== e.target.value) {
                      form.setFieldsValue({ companyName: value });
                    }
                  }}
                />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Corporate/Legal Name" name="corporateName">
                <Input placeholder="Bitmaker E.I.R.L." />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem
                label="Email Address"
                name="emailAddress"
                rules={[{ type: "email", message: "Invalid email format" }]}
              >
                <Input placeholder="info@bitmaker.la" />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem
                label="Billing Email"
                name="billingEmailAddress"
                tooltip="Email for invoice sending/notifications."
                rules={[{ type: "email", message: "Invalid email format" }]}
              >
                <Input placeholder="payments@bitmaker.la" />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Phone Number" name="phoneNumber">
                <Input placeholder="+1 555-123-4567" />
              </MyFormItem>
            </Col>
          </Row>
        </section>

        {/* Section 2: Tax & Legal Information */}
        <section className="p-6 bg-white shadow rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Tax & Legal Information
          </h3>

          <Row gutter={24}>
            <Col span={12}>
              <MyFormItem label="Tax Name (e.g., RUC)" name="taxName">
                <Input placeholder="RUC" />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Tax Number (ID)" name="taxNumber">
                <Input placeholder="10203040506" />
              </MyFormItem>
            </Col>
          </Row>
        </section>

        {/* Section 3: Physical Address */}
        <section className="p-6 bg-white shadow rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Physical Address
          </h3>

          <Row gutter={[24, 16]}>
            <Col span={24}>
              <MyFormItem label="Address Line" name="addressLine">
                <Input.TextArea
                  rows={2}
                  placeholder="Street Address, Apt/Unit"
                />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Town/City" name="townCity">
                <Input placeholder="Arequipa" />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="State/Province" name="stateProvince">
                <Input placeholder="Arequipa" />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Country" name="country">
                <Select
                  placeholder="Select Country"
                  options={countryOptions}
                  showSearch
                  allowClear
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem label="Postal Code" name="postalCode">
                <Input placeholder="04001" />
              </MyFormItem>
            </Col>
          </Row>
        </section>

        {/* Section 4: Defaults & Notes */}
        <section className="p-6 bg-white shadow rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Defaults & Notes
          </h3>

          <Row gutter={[24, 16]}>
            <Col span={12}>
              <MyFormItem
                label="Default Currency"
                name="defaultCurrency"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  options={CURRENCY_OPTIONS}
                  placeholder="Select currency"
                />
              </MyFormItem>
            </Col>

            <Col span={12}>
              <MyFormItem
                label="Default Language"
                name="defaultLanguage"
                rules={[{ required: true, message: "Required" }]}
              >
                <Select
                  options={LANGUAGE_OPTIONS}
                  placeholder="Select language"
                />
              </MyFormItem>
            </Col>

            <Col span={24}>
              <MyFormItem
                label="Default Term"
                name="defaultTermId"
                tooltip="Default payment terms to apply to new invoices."
              >
                <Select
                  placeholder="Select a default term"
                  options={terms.map((term) => ({
                    value: term.id,
                    label: term.name,
                  }))}
                  disabled={
                    loadingTerms || !!fetchTermsError || terms.length === 0
                  }
                  allowClear
                />
              </MyFormItem>

              {fetchTermsError && (
                <p className="text-xs text-red-500 mt-1">{fetchTermsError}</p>
              )}

              {!fetchTermsError && terms.length === 0 && (
                <p className="text-xs text-red-500 mt-1">
                  No terms available. Define terms in the Terms section.
                </p>
              )}
            </Col>

            <Col span={24}>
              <MyFormItem
                label="Default Notes"
                name="defaultNotes"
                tooltip="Notes automatically added to invoices."
              >
                <Input.TextArea
                  rows={4}
                  placeholder="Bank transfer information, additional legal notes..."
                />
              </MyFormItem>
            </Col>
          </Row>
        </section>

        {/* Save Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200 sticky bottom-0 bg-white z-10 shadow-lg -mx-6 px-6">
          <button
            type="submit"
            form="companyInfoForm"
            disabled={loading}
            className="btn btn-green h-10 mb-4"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Check size={18} />
            )}
            <span>{loading ? "Saving..." : "Save Settings"}</span>
          </button>
        </div>
      </Form>
    </div>
  );
}
